import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Collapse, HStack, Text } from "@chakra-ui/react";
import type { LogEntry, OrderSnapshot } from "../flow/types";
import { formatOrderRefs } from "../config/executionHistoryStore";
import { copyText, formatClock } from "./statusMeta";
import { formatDuration } from "../flow/estimateTime";
import useNotification from "../../../utils/useNotification";

interface Props {
  log: LogEntry[];
  snapshot?: OrderSnapshot;
  defaultOpen?: boolean;
  forceOpenKey?: number;
}

const LEVEL_COLOR: Record<LogEntry["level"], string> = {
  info: "gray.200",
  success: "green.300",
  warn: "yellow.300",
  error: "red.300",
  debug: "gray.500",
};

const ExecutionLog: React.FC<Props> = ({ log, snapshot, defaultOpen = false, forceOpenKey }) => {
  const toast = useNotification();
  const [open, setOpen] = useState(defaultOpen);
  const bottomRef = useRef<HTMLDivElement>(null);
  const refs = formatOrderRefs({
    enquiryNo: snapshot?.enquiry?.orderNo,
    enquiryId: snapshot?.enquiry?.orderId,
    orderNo: snapshot?.child?.orderNo,
    orderId: snapshot?.child?.orderId,
  });

  useEffect(() => {
    if (forceOpenKey) setOpen(true);
  }, [forceOpenKey]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [log.length, open]);

  const text = [refs, ...log.map((e) => `[${formatClock(e.at)} +${formatDuration(e.offsetMs)}] ${e.level.toUpperCase().padEnd(7)} ${e.stepId ? `(${e.stepId}) ` : ""}${e.message}`)]
    .filter(Boolean)
    .join("\n");

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      <HStack px={4} py={2.5} justify="space-between" cursor="pointer" onClick={() => setOpen((o) => !o)} _hover={{ bg: "gray.50" }}>
        <HStack>
          <Text fontWeight="700" fontSize="sm">
            Execution log
          </Text>
          <Text fontSize="10px" color="gray.400" fontFamily="mono">
            {log.length} entries
          </Text>
        </HStack>
        <HStack>
          <Button
            size="xs"
            variant="ghost"
            isDisabled={!log.length}
            onClick={async (e) => {
              e.stopPropagation();
              const ok = await copyText(text);
              toast({ status: ok ? "success" : "error", description: ok ? "Log copied" : "Copy failed" });
            }}
          >
            Copy
          </Button>
          <Text fontSize="xs" color="gray.500">
            {open ? "Hide" : "Show"}
          </Text>
        </HStack>
      </HStack>
      <Collapse in={open} animateOpacity>
        {refs && (
          <Box bg="gray.800" color="blue.200" fontFamily="mono" fontSize="11px" px={4} py={2} borderBottomWidth="1px" borderColor="gray.700">
            {refs}
          </Box>
        )}
        <Box bg="gray.900" color="gray.100" fontFamily="mono" fontSize="11px" maxH="320px" overflowY="auto" px={4} py={3}>
          {log.length === 0 && <Text color="gray.500">No log entries yet.</Text>}
          {log.map((e) => (
            <HStack key={e.id} align="baseline" spacing={3} py="1px">
              <Text color="gray.500" flexShrink={0}>
                {formatClock(e.at)}
              </Text>
              <Text color="gray.600" flexShrink={0} w="56px" textAlign="right">
                +{formatDuration(e.offsetMs)}
              </Text>
              <Text color={LEVEL_COLOR[e.level]} whiteSpace="pre-wrap" wordBreak="break-word">
                {e.stepId && (
                  <Text as="span" color="gray.500">
                    [{e.stepId}]{" "}
                  </Text>
                )}
                {e.message}
              </Text>
            </HStack>
          ))}
          <div ref={bottomRef} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default ExecutionLog;
